import { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { SmartCouponSection } from './SmartCouponSection';
import { CouponInput } from './CouponInput';
import { CouponAppliedBadge } from './CouponAppliedBadge';
import { AvailableCouponsDrawer } from './AvailableCouponsDrawer';
import { useValidateCoupon, useAvailableCoupons } from '../../coupons/hooks/useCoupons';
import { useQueryClient } from '@tanstack/react-query';
import type { CouponValidationResult } from '@lov/shared';

interface CouponSectionProps {
  venueId: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  subtotal: number;
  appliedCoupon: CouponValidationResult | null;
  onApplyCoupon: (result: CouponValidationResult) => void;
  onRemoveCoupon: () => void;
}

export function CouponSection({
  venueId,
  bookingDate,
  startTime,
  endTime,
  subtotal,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponSectionProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [applyingCode, setApplyingCode] = useState<string | undefined>();
  const queryClient = useQueryClient();

  // Fetch available coupons
  const { data: availableCouponsData, isLoading: isLoadingCoupons } = useAvailableCoupons(
    {
      venueId,
      bookingDate,
      startTime,
      subtotal,
    },
    {
      enabled: !!venueId && !!bookingDate,
    }
  );

  // Validate coupon mutation
  const validateCouponMutation = useValidateCoupon();

  const handleApplyCoupon = async (code: string) => {
    setValidationError(null);
    setApplyingCode(code);

    try {
      const result = await validateCouponMutation.mutateAsync({
        code,
        venueId,
        bookingDate,
        startTime,
        endTime,
        subtotal,
      });

      if (result.isValid) {
        onApplyCoupon(result);
        setDrawerOpen(false);
      } else {
        setValidationError(result.message);
        void queryClient.invalidateQueries({ queryKey: ['coupons', 'available'] });
      }
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to validate coupon';
      setValidationError(errorMessage);
    } finally {
      setApplyingCode(undefined);
    }
  };

  const handleRemoveCoupon = () => {
    setValidationError(null);
    onRemoveCoupon();
  };

  // Combine all coupons for drawer
  const allCoupons = [
    ...(availableCouponsData?.availableCoupons || []),
    ...(availableCouponsData?.nearlyUnlockedCoupons || []),
    ...(availableCouponsData?.lockedCoupons || []),
  ];

  // If coupon is applied, show badge only
  if (appliedCoupon && appliedCoupon.isValid && appliedCoupon.coupon) {
    return (
      <Box sx={{ mb: 2 }}>
        <CouponAppliedBadge
          code={appliedCoupon.coupon.code}
          discountAmount={appliedCoupon.discountAmount || 0}
          onRemove={handleRemoveCoupon}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      {/* Smart expandable coupon section */}
      {availableCouponsData && (
        <SmartCouponSection
          couponsData={availableCouponsData}
          onApplyCoupon={handleApplyCoupon}
          appliedCouponCode={appliedCoupon?.coupon?.code}
          applyingCouponCode={applyingCode}
        />
      )}

      {/* Manual input fallback */}
      {!isLoadingCoupons && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <CouponInput
            onApply={handleApplyCoupon}
            isLoading={validateCouponMutation.isPending}
            error={validationError || undefined}
          />
        </Paper>
      )}

      {/* Available coupons drawer */}
      <AvailableCouponsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        coupons={allCoupons}
        isLoading={isLoadingCoupons}
        onApplyCoupon={handleApplyCoupon}
      />
    </Box>
  );
}
